import React, { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../../config";
import {
    Building2, Plus, Trash2, Edit, Users, UserCheck,
    X, CheckCircle, AlertCircle, ChevronLeft, ChevronRight,
    RefreshCw, Filter, Save, Search, BookOpen, GraduationCap,
    ChevronDown,
} from "lucide-react";
import ActionIconButton from "../../Components/ActionIconButton";
import ActionsCell from "../../Components/ActionsCell";

const PAGE_SIZE = 20;

const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem("accessToken");
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...options.headers },
        ...options,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
    }
    if (res.status === 204) return null;
    return res.json();
};

const DepartmentManagement = () => {
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState(null);
    const [success, setSuccess]   = useState(null);

    const [departments, setDepartments] = useState([]);
    const [hodUsers, setHodUsers]       = useState([]);
    const [allTeachers, setAllTeachers] = useState([]);
    const [page, setPage]               = useState(1);
    const [search, setSearch]           = useState("");
    const [hodFilter, setHodFilter]     = useState("");

    // Create / Edit modal
    const [showModal, setShowModal]     = useState(false);
    const [editing, setEditing]         = useState(null); // null = create, obj = edit
    const [form, setForm]               = useState({ name: "", subjects: "", grade_levels: "" });
    const [saving, setSaving]           = useState(false);

    // Assign HOD modal
    const [hodModal, setHodModal]       = useState(null); // dept obj
    const [selectedHodId, setSelectedHodId] = useState("");

    // Manage Teachers modal
    const [teachersModal, setTeachersModal]   = useState(null); // dept obj
    const [teacherSearch, setTeacherSearch]   = useState("");
    const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);

    // Delete confirm modal
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting]         = useState(false);

    // ── Notify ──────────────────────────────────────────────────────────────────
    const notify = (type, msg) => {
        if (type === "success") { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); }
        else { setError(msg); setTimeout(() => setError(null), 5000); }
    };

    // ── Fetch ────────────────────────────────────────────────────────────────────
    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [depts, hods, teachers] = await Promise.all([
                apiCall("/admin/departments/"),
                apiCall("/admin/staff/role/hod").catch(() => []),
                apiCall("/admin/get-teachers").catch(() => []),
            ]);
            setDepartments(Array.isArray(depts) ? depts : []);
            setHodUsers(Array.isArray(hods) ? hods : []);
            setAllTeachers(Array.isArray(teachers) ? teachers : []);
        } catch (e) {
            notify("error", `Failed to load: ${e.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    // ── Filtered / Paginated ──────────────────────────────────────────────────
    const filtered = departments.filter(d =>
        (!search ||
            d.name?.toLowerCase().includes(search.toLowerCase()) ||
            d.hod_name?.toLowerCase().includes(search.toLowerCase()) ||
            d.subjects?.some(s => s.toLowerCase().includes(search.toLowerCase()))) &&
        (!hodFilter || d.hod_name === hodFilter)
    );
    const total      = filtered.length;
    const totalPages = Math.ceil(total / PAGE_SIZE);
    const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // Distinct HOD names across loaded departments
    const distinctHods = Array.from(
        new Set(departments.map(d => d.hod_name).filter(Boolean))
    ).sort();

    // Stats
    const stats = {
        total:        departments.length,
        with_hod:     departments.filter(d => d.hod_id).length,
        total_teachers: departments.reduce((s, d) => s + (d.teacher_count || 0), 0),
        no_hod:       departments.filter(d => !d.hod_id).length,
    };

    // ── Create / Edit ─────────────────────────────────────────────────────────
    const openCreate = () => {
        setEditing(null);
        setForm({ name: "", subjects: "", grade_levels: "" });
        setShowModal(true);
    };

    const openEdit = (dept) => {
        setEditing(dept);
        setForm({
            name: dept.name || "",
            subjects: dept.subjects?.join(", ") || "",
            grade_levels: dept.grade_levels?.join(", ") || "",
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) { notify("error", "Department name is required"); return; }
        setSaving(true);
        try {
            const body = {
                name: form.name.trim(),
                subjects: form.subjects ? form.subjects.split(",").map(s => s.trim()).filter(Boolean) : [],
                grade_levels: form.grade_levels ? form.grade_levels.split(",").map(s => s.trim()).filter(Boolean) : [],
            };
            if (editing) {
                await apiCall(`/admin/departments/${editing.id}`, { method: "PUT", body: JSON.stringify(body) });
                notify("success", "Department updated successfully");
            } else {
                await apiCall("/admin/departments/", { method: "POST", body: JSON.stringify(body) });
                notify("success", "Department created successfully");
            }
            setShowModal(false);
            await loadAll();
        } catch (e) { notify("error", e.message); }
        finally { setSaving(false); }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await apiCall(`/admin/departments/${deleteTarget.id}`, { method: "DELETE" });
            notify("success", `"${deleteTarget.name}" deleted`);
            setDeleteTarget(null);
            await loadAll();
        } catch (e) { notify("error", e.message); }
        finally { setDeleting(false); }
    };

    // ── Assign HOD ────────────────────────────────────────────────────────────
    const openHodModal = (dept) => {
        setHodModal(dept);
        setSelectedHodId(dept.hod_id || "");
    };

    const handleAssignHOD = async () => {
        if (!selectedHodId) return;
        setSaving(true);
        try {
            await apiCall(`/admin/departments/${hodModal.id}/assign-hod`, {
                method: "POST",
                body: JSON.stringify({ hod_id: selectedHodId }),
            });
            notify("success", "HOD assigned successfully");
            setHodModal(null);
            await loadAll();
        } catch (e) { notify("error", e.message); }
        finally { setSaving(false); }
    };

    // ── Manage Teachers ───────────────────────────────────────────────────────
    const openTeachersModal = (dept) => {
        setTeachersModal(dept);
        setSelectedTeacherIds([...(dept.teacher_ids || [])]);
        setTeacherSearch("");
    };

    const toggleTeacher = (tid) => {
        setSelectedTeacherIds(prev =>
            prev.includes(tid) ? prev.filter(id => id !== tid) : [...prev, tid]
        );
    };

    const handleSaveTeachers = async () => {
        setSaving(true);
        try {
            const current = teachersModal.teacher_ids || [];
            const toAdd    = selectedTeacherIds.filter(id => !current.includes(id));
            const toRemove = current.filter(id => !selectedTeacherIds.includes(id));
            await Promise.all([
                ...(toAdd.length ? [apiCall(`/admin/departments/${teachersModal.id}/teachers`, {
                    method: "POST", body: JSON.stringify({ teacher_ids: toAdd }),
                })] : []),
                ...toRemove.map(tid =>
                    apiCall(`/admin/departments/${teachersModal.id}/teachers/${tid}`, { method: "DELETE" })
                ),
            ]);
            notify("success", "Teachers updated successfully");
            setTeachersModal(null);
            await loadAll();
        } catch (e) { notify("error", e.message); }
        finally { setSaving(false); }
    };

    const filteredTeachers = allTeachers.filter(t =>
        !teacherSearch ||
        t.name?.toLowerCase().includes(teacherSearch.toLowerCase()) ||
        t.email?.toLowerCase().includes(teacherSearch.toLowerCase())
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

                    {/* Toasts */}
                    {error && (
                        <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 bg-red-500 text-white" style={{ animation: "slideInRight 0.3s ease-out" }}>
                            <AlertCircle className="w-5 h-5" /><span>{error}</span>
                            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
                        </div>
                    )}
                    {success && (
                        <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 bg-green-500 text-white" style={{ animation: "slideInRight 0.3s ease-out" }}>
                            <CheckCircle className="w-5 h-5" /><span>{success}</span>
                            <button onClick={() => setSuccess(null)}><X className="w-4 h-4" /></button>
                        </div>
                    )}

                    {/* ── Hero Header ──────────────────────────────────────────────── */}
                    <div className="mb-8">
                        <div className="bg-gradient-to-r from-orange-50 to-blue-50 rounded-2xl p-8 border border-orange-100">
                            <div className="flex items-start gap-6">
                                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#D67C2C] to-orange-600 flex items-center justify-center shadow-lg flex-shrink-0">
                                    <Building2 className="w-10 h-10 text-white" />
                                </div>
                                <div className="flex-1">
                                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Department Management</h1>
                                    <p className="text-gray-600 text-lg mb-4">Create departments, assign HODs and manage teacher allocations across your school</p>
                                    <div className="flex flex-wrap gap-4 text-sm">
                                        <div className="flex items-center gap-2 text-gray-700"><div className="w-2 h-2 rounded-full bg-[#D67C2C]"></div><span>{stats.total} departments</span></div>
                                        <div className="flex items-center gap-2 text-gray-700"><div className="w-2 h-2 rounded-full bg-green-500"></div><span>{stats.with_hod} HODs assigned</span></div>
                                        <div className="flex items-center gap-2 text-gray-700"><div className="w-2 h-2 rounded-full bg-blue-500"></div><span>{stats.total_teachers} teachers allocated</span></div>
                                    </div>
                                </div>
                                <button
                                    onClick={openCreate}
                                    className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-[#D67C2C] to-orange-600 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-200 flex-shrink-0"
                                >
                                    <Plus className="w-5 h-5" /> New Department
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ── Stats ────────────────────────────────────────────────────── */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        {[
                            { label: "Total Departments",    value: stats.total,          icon: <Building2 className="w-6 h-6 text-[#D67C2C]" />,   bg: "bg-orange-100" },
                            { label: "HODs Assigned",        value: stats.with_hod,        icon: <UserCheck className="w-6 h-6 text-green-600" />,     bg: "bg-green-100"  },
                            { label: "Teachers Allocated",   value: stats.total_teachers,  icon: <Users className="w-6 h-6 text-blue-600" />,          bg: "bg-blue-100"   },
                            { label: "Without HOD",          value: stats.no_hod,          icon: <AlertCircle className="w-6 h-6 text-amber-600" />,   bg: "bg-amber-100"  },
                        ].map(({ label, value, icon, bg }) => (
                            <div key={label} className="bg-white rounded-xl shadow-md border border-gray-200 p-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">{label}</p>
                                        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
                                    </div>
                                    <div className={`p-3 rounded-full ${bg}`}>{icon}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ── Filters ───────────────────────────────────────────────────── */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Filter className="w-5 h-5 text-[#D67C2C]" />
                            <h3 className="font-semibold text-gray-800">Filters</h3>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <div className="relative flex-1 min-w-[200px]">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by name, HOD, subject..."
                                    value={search}
                                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                />
                            </div>
                            <select
                                value={hodFilter}
                                onChange={e => { setHodFilter(e.target.value); setPage(1); }}
                                className="min-w-[180px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                            >
                                <option value="">All HODs</option>
                                {distinctHods.map(name => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>
                        </div>
                        {(search || hodFilter) && (
                            <button onClick={() => { setSearch(""); setHodFilter(""); setPage(1); }}
                                className="mt-3 text-sm text-[#D67C2C] hover:underline font-medium">
                                Clear filters
                            </button>
                        )}
                    </div>

                    {/* ── Table ────────────────────────────────────────────────────── */}
                    <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                        <div className="p-5 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">All Departments</h2>
                                <p className="text-sm text-gray-500 mt-0.5">{total} department{total !== 1 ? "s" : ""} found</p>
                            </div>
                            <button onClick={loadAll}
                                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                                <RefreshCw className="w-4 h-4" /> Refresh
                            </button>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-16">
                                <RefreshCw className="w-8 h-8 text-[#D67C2C] animate-spin" />
                                <span className="ml-3 text-gray-600">Loading departments...</span>
                            </div>
                        ) : paginated.length === 0 ? (
                            <div className="text-center py-16">
                                <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 text-lg font-medium">No departments found</p>
                                <p className="text-gray-400 text-sm mt-1">
                                    {(search || hodFilter) ? "Try adjusting your search or filters" : "Create your first department to get started"}
                                </p>
                                {!search && !hodFilter && (
                                    <button onClick={openCreate}
                                        className="mt-4 flex items-center gap-2 px-4 py-2 bg-[#D67C2C] text-white rounded-lg mx-auto hover:bg-orange-600 transition-colors">
                                        <Plus className="w-4 h-4" /> Create Department
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            {["Department", "HOD", "Subjects", "Grades", "Teachers", "Actions"].map(h => (
                                                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        {paginated.map(dept => (
                                            <tr key={dept.id} className="hover:bg-gray-50 transition-colors">
                                                {/* Department name */}
                                                <td className="px-4 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D67C2C] to-orange-600 flex items-center justify-center flex-shrink-0">
                                                            <Building2 className="w-4 h-4 text-white" />
                                                        </div>
                                                        <div>
                                                            <div className="font-medium text-gray-900 text-sm">{dept.name}</div>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* HOD */}
                                                <td className="px-4 py-4">
                                                    {dept.hod_id ? (
                                                        <div>
                                                            <div className="flex items-center gap-1.5">
                                                                <UserCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                                                <span className="text-sm font-medium text-gray-900">{dept.hod_name}</span>
                                                            </div>
                                                            <div className="text-xs text-gray-400 mt-0.5 ml-5">{dept.hod_email}</div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full font-medium">Unassigned</span>
                                                    )}
                                                </td>

                                                {/* Subjects */}
                                                <td className="px-4 py-4">
                                                    <div className="flex flex-wrap gap-1 max-w-[160px]">
                                                        {dept.subjects?.length > 0
                                                            ? dept.subjects.map(s => (
                                                                <span key={s} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">{s}</span>
                                                            ))
                                                            : <span className="text-xs text-gray-400">—</span>}
                                                    </div>
                                                </td>

                                                {/* Grades */}
                                                <td className="px-4 py-4">
                                                    <div className="flex flex-wrap gap-1 max-w-[140px]">
                                                        {dept.grade_levels?.length > 0
                                                            ? dept.grade_levels.map(g => (
                                                                <span key={g} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{g}</span>
                                                            ))
                                                            : <span className="text-xs text-gray-400">—</span>}
                                                    </div>
                                                </td>

                                                {/* Teachers */}
                                                <td className="px-4 py-4">
                                                    <span className="text-sm font-semibold text-gray-800">{dept.teacher_count ?? 0}</span>
                                                    <span className="text-xs text-gray-400 ml-1">teacher{dept.teacher_count !== 1 ? "s" : ""}</span>
                                                </td>

                                                {/* Actions */}
                                                <td className="px-4 py-4">
                                                    <ActionsCell>
                                                        <ActionIconButton icon={Edit} tone="edit" onClick={() => openEdit(dept)} title="Edit" />
                                                        <ActionIconButton icon={UserCheck} tone="assign" onClick={() => openHodModal(dept)} title="Assign HOD" />
                                                        <ActionIconButton icon={Users} tone="link" onClick={() => openTeachersModal(dept)} title="Manage Teachers" />
                                                        <ActionIconButton icon={Trash2} tone="delete" onClick={() => setDeleteTarget(dept)} title="Delete" />
                                                    </ActionsCell>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                                <p className="text-sm text-gray-600">
                                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                                </p>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1}
                                        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let n;
                                        if (totalPages <= 5) n = i + 1;
                                        else if (page <= 3) n = i + 1;
                                        else if (page >= totalPages - 2) n = totalPages - 4 + i;
                                        else n = page - 2 + i;
                                        return (
                                            <button key={n} onClick={() => setPage(n)}
                                                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${page === n ? "bg-[#D67C2C] text-white" : "text-gray-700 hover:bg-gray-100"}`}>
                                                {n}
                                            </button>
                                        );
                                    })}
                                    <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages}
                                        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {/* ══════════════════ MODALS ══════════════════ */}

            {/* Create / Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-[#D67C2C] flex items-center justify-center">
                                    <Building2 className="w-5 h-5 text-white" />
                                </div>
                                <h2 className="text-xl font-semibold text-gray-900">
                                    {editing ? "Edit Department" : "Create New Department"}
                                </h2>
                            </div>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Department Name <span className="text-red-500">*</span></label>
                                <input type="text" value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    placeholder="e.g. Science Department"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Subjects <span className="text-gray-400 font-normal text-xs">(comma-separated)</span>
                                </label>
                                <input type="text" value={form.subjects}
                                    onChange={e => setForm(f => ({ ...f, subjects: e.target.value }))}
                                    placeholder="e.g. Physics, Chemistry, Biology"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Grade Levels <span className="text-gray-400 font-normal text-xs">(comma-separated)</span>
                                </label>
                                <input type="text" value={form.grade_levels}
                                    onChange={e => setForm(f => ({ ...f, grade_levels: e.target.value }))}
                                    placeholder="e.g. Grade 6, Grade 7, Grade 8"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent" />
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100">
                            <button onClick={() => setShowModal(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors">
                                Cancel
                            </button>
                            <button onClick={handleSave} disabled={saving || !form.name.trim()}
                                className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-[#D67C2C] to-orange-600 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 disabled:opacity-60">
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                {saving ? "Saving..." : (editing ? "Save Changes" : "Create Department")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign HOD Modal */}
            {hodModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-[#D67C2C] flex items-center justify-center">
                                    <UserCheck className="w-5 h-5 text-white" />
                                </div>
                                <h2 className="text-xl font-semibold text-gray-900">Assign HOD</h2>
                            </div>
                            <button onClick={() => setHodModal(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-gray-500 mb-4">
                                Select Head of Department for <span className="font-semibold text-gray-900">{hodModal.name}</span>
                            </p>
                            <div className="relative">
                                <select value={selectedHodId} onChange={e => setSelectedHodId(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent appearance-none bg-white pr-9">
                                    <option value="">— Select HOD —</option>
                                    {hodUsers.map(h => (
                                        <option key={h.id || h._id} value={h.id || h._id}>
                                            {h.name} ({h.email})
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            </div>
                            {hodUsers.length === 0 && (
                                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                                    No HOD users found. Create HOD accounts first from User Management.
                                </p>
                            )}
                        </div>
                        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100">
                            <button onClick={() => setHodModal(null)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors">Cancel</button>
                            <button onClick={handleAssignHOD} disabled={saving || !selectedHodId}
                                className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-[#D67C2C] to-orange-600 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 disabled:opacity-60">
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                {saving ? "Saving..." : "Assign HOD"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manage Teachers Modal */}
            {teachersModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-[#D67C2C] flex items-center justify-center">
                                    <Users className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">Manage Teachers</h2>
                                    <p className="text-sm text-gray-500">{teachersModal.name}</p>
                                </div>
                            </div>
                            <button onClick={() => setTeachersModal(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Search + count */}
                        <div className="p-4 border-b border-gray-100">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input type="text" value={teacherSearch}
                                    onChange={e => setTeacherSearch(e.target.value)}
                                    placeholder="Search teachers..."
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent" />
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                                <span className="font-semibold text-[#D67C2C]">{selectedTeacherIds.length}</span> of {allTeachers.length} teacher{allTeachers.length !== 1 ? "s" : ""} selected
                            </p>
                        </div>

                        {/* Teacher list */}
                        <div className="overflow-y-auto max-h-72">
                            {filteredTeachers.length === 0 ? (
                                <div className="text-center py-10 text-gray-400 text-sm">No teachers found</div>
                            ) : (
                                <table className="w-full">
                                    <tbody className="divide-y divide-gray-100">
                                        {filteredTeachers.map(t => {
                                            const tid = t.id || t._id;
                                            const checked = selectedTeacherIds.includes(tid);
                                            return (
                                                <tr key={tid}
                                                    onClick={() => toggleTeacher(tid)}
                                                    className={`cursor-pointer transition-colors ${checked ? "bg-orange-50" : "hover:bg-gray-50"}`}>
                                                    <td className="px-4 py-3 w-10">
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${checked ? "bg-[#D67C2C] text-white" : "bg-gray-200 text-gray-600"}`}>
                                                            {t.name?.[0]?.toUpperCase() || "T"}
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-3">
                                                        <div className="text-sm font-medium text-gray-900">{t.name}</div>
                                                        <div className="text-xs text-gray-400">{t.email}</div>
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        {checked && <CheckCircle className="w-5 h-5 text-[#D67C2C] ml-auto" />}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100">
                            <button onClick={() => setTeachersModal(null)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors">Cancel</button>
                            <button onClick={handleSaveTeachers} disabled={saving}
                                className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-[#D67C2C] to-orange-600 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 disabled:opacity-60">
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                {saving ? "Saving..." : "Save Teachers"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm Modal */}
            {deleteTarget && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                <Trash2 className="w-6 h-6 text-red-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Delete Department</h3>
                                <p className="text-sm text-gray-500">This action cannot be undone</p>
                            </div>
                        </div>
                        <p className="text-gray-700 mb-2">
                            Are you sure you want to delete <span className="font-semibold">"{deleteTarget.name}"</span>?
                        </p>
                        <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3 mb-6">
                            This will remove the department and unassign all <strong>{deleteTarget.teacher_count || 0} teacher(s)</strong> and the HOD from it.
                        </p>
                        <div className="flex items-center justify-end gap-3">
                            <button onClick={() => setDeleteTarget(null)} disabled={deleting}
                                className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium">Cancel</button>
                            <button onClick={handleDelete} disabled={deleting}
                                className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-60">
                                {deleting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                {deleting ? "Deleting..." : "Delete Department"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};

export default DepartmentManagement;
